import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Sun, Moon, BookOpen, ArrowRight } from 'lucide-react';

export default function LoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const { login } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        try {
            await login(email, password);
            navigate('/dashboard');
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to login');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-split">
            {/* Image Side */}
            <div className="login-image-side">
                <img
                    src="https://images.unsplash.com/photo-1547459671-6d95eed7af1b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxsaWJyYXJ5JTIwYXJjaGl0ZWN0dXJlJTIwc3R1ZHl8ZW58MHx8fHwxNzc0MzA4Mzc1fDA&ixlib=rb-4.1.0&q=85"
                    alt="Architectural library"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/20 noise-overlay" />
                <div className="absolute bottom-12 left-12 right-12">
                    <h1 className="font-serif text-4xl lg:text-5xl text-white tracking-tight mb-4">
                        Master your<br />knowledge.
                    </h1>
                    <p className="font-sans text-white/80 text-lg">
                        Flashcards designed for deep focus and retention.
                    </p>
                </div>
            </div>

            {/* Form Side */}
            <div className="login-form-side relative">
                <Button
                    variant="ghost"
                    size="icon"
                    onClick={toggleTheme}
                    data-testid="theme-toggle-login"
                    className="absolute top-6 right-6"
                >
                    {theme === 'light' ? (
                        <Moon className="w-5 h-5" strokeWidth={1.5} />
                    ) : (
                        <Sun className="w-5 h-5" strokeWidth={1.5} />
                    )}
                </Button>

                <div className="w-full max-w-sm">
                    <div className="flex items-center gap-3 mb-12">
                        <div className="w-10 h-10 bg-primary flex items-center justify-center">
                            <BookOpen className="w-6 h-6 text-primary-foreground" strokeWidth={1.5} />
                        </div>
                        <span className="font-serif text-2xl tracking-tight">flashKard</span>
                    </div>

                    <h2 className="font-serif text-3xl tracking-tight mb-2">Welcome back</h2>
                    <p className="font-sans text-muted-foreground mb-8">
                        Sign in to continue learning
                    </p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {error && (
                            <div 
                                data-testid="login-error"
                                className="p-4 bg-destructive/10 border border-destructive/30 text-destructive text-sm"
                            >
                                {error}
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="email" className="font-mono text-xs tracking-wider uppercase">
                                Email
                            </Label>
                            <Input
                                id="email"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                required
                                data-testid="login-email-input"
                                className="rounded-none"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password" className="font-mono text-xs tracking-wider uppercase">
                                Password
                            </Label>
                            <Input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter your password"
                                required
                                data-testid="login-password-input"
                                className="rounded-none"
                            />
                        </div>

                        <Button
                            type="submit"
                            disabled={loading}
                            data-testid="login-submit-button"
                            className="w-full rounded-none btn-lift h-12"
                        >
                            {loading ? 'Signing in...' : 'Sign in'}
                            <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.5} />
                        </Button>
                    </form>

                    <p className="mt-8 text-center text-sm text-muted-foreground">
                        Don't have an account?{' '}
                        <Link 
                            to="/signup" 
                            data-testid="signup-link"
                            className="text-primary hover:underline underline-offset-4"
                        >
                            Create one
                        </Link>
                    </p>
                </div>
            </div>
        </div>
    );
}
